import FormikExtended from '@sprinx/react-forms/FormikExtended';
import WrapDialogForm from '@sprinx/react-mui-components/WrapDialogForm';
import PriceField from '@sprinx/react-mui-fields/PriceField';
import CheckboxField from '@sprinx/react-mui-fields/CheckboxField';
import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { apiClientState, currencyState } from '../../../api/appState';
import { enqueueSnackbarMessage } from '../../../api/snackbars';
import {
  ContactTotalOrderLimit,
  getContactTotalOrderLimit,
  removeContactTotalOrderLimit,
  upsertContactTotalOrderLimit,
} from '../../../api/customer/contactTotalOrderLimit';
import { ContactSupervisor, getContactSupervisors } from '../../../api/customer/contactSupervisors';
import usePromise from '../../../hooks/usePromise';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import CustomerProfilePageSubsidiarySupervisorSelector from './CustomerProfilePageSubsidiarySupervisorSelector';

export interface CustomerProfilePageSubsidiaryEditDialogProps {
  customerId: string;
  isMaster?: boolean;
  onClose: () => void;
  onFormReleased: (p: any) => any;
  open: boolean;
}

export interface CustomerProfileEdit {
  isSupervisionActivated: boolean;
  limit: number | undefined;
  supervisor: string | undefined;
}

export interface CustomerProfileEditErrors {
  isSupervisionActivated: boolean;
  limit: string | undefined;
  supervisor: string | undefined;
}

export interface CustomerProfileEditTouched {
  isSupervisionActivated: boolean;
  limit: number | undefined;
  supervisor: string | undefined;
}

const CustomerProfilePageSubsidiaryEditDialog: React.FC<CustomerProfilePageSubsidiaryEditDialogProps> = ({
  customerId,
  open,
  onClose,
  onFormReleased,
}) => {
  const t = useTranslateWithFallback();
  const currency = useRecoilValue(currencyState);
  const showMessage = useSetRecoilState(enqueueSnackbarMessage);
  const apiClient = useRecoilValue(apiClientState);

  const resTotalOrderLimit = usePromise<ContactTotalOrderLimit | null, unknown>(() =>
    getContactTotalOrderLimit(apiClient, customerId),
  );

  const resContactSupervisors = usePromise<ContactSupervisor[], unknown>(() =>
    getContactSupervisors(apiClient, customerId),
  );

  const hasError = resTotalOrderLimit.error || resContactSupervisors.error;

  const initialValues = React.useMemo(() => {
    if (!resTotalOrderLimit.data) {
      return {
        isSupervisionActivated: false,
        supervisor: undefined,
        limit: undefined,
      };
    }
    return {
      isSupervisionActivated: true,
      supervisor: resTotalOrderLimit.data.supervisorId,
      limit: resTotalOrderLimit.data.totalOrderLimit,
    };
  }, [resTotalOrderLimit.data]);

  const handleSave = React.useCallback(
    async ({ supervisor, limit, isSupervisionActivated }: CustomerProfileEdit) => {
      if (isSupervisionActivated) {
        if (!supervisor || typeof limit !== 'number' || limit <= 0) {
          showMessage({
            message: t('customerProfilePage/changeSubsidiary/messages/upsertTotalOrderLimit/error'),
            variant: 'error',
          });
          return;
        }
        await upsertContactTotalOrderLimit(apiClient, {
          contactId: customerId,
          supervisorId: supervisor,
          totalOrderLimit: limit,
        })
          .then(() => {
            showMessage({
              message: t('customerProfilePage/changeSubsidiary/messages/upsertTotalOrderLimit/success'),
              variant: 'success',
            });
          })
          .catch(() => {
            showMessage({
              message: t('customerProfilePage/changeSubsidiary/messages/upsertTotalOrderLimit/error'),
              variant: 'error',
            });
          });
        return;
      } else {
        await removeContactTotalOrderLimit(apiClient, customerId)
          .then(() => {
            showMessage({
              message: t('customerProfilePage/changeSubsidiary/messages/removeTotalOrderLimit/success'),
              variant: 'success',
            });
          })
          .catch(() => {
            showMessage({
              message: t('customerProfilePage/changeSubsidiary/messages/removeTotalOrderLimit/error'),
              variant: 'error',
            });
          });
      }
    },
    [apiClient, customerId, showMessage, t],
  );

  if (hasError) {
    return null;
  }

  return (
    <FormikExtended<CustomerProfileEdit, CustomerProfileEditErrors, CustomerProfileEditTouched>
      onFormReleased={onFormReleased}
      onSave={handleSave}
      initialValues={initialValues}
      enableReinitialize
      validationFastest={{
        messages: {
          supervisor: 'customerProfilePage/changeSubsidiary/messages/formValidation/supervisor',
          limit: 'customerProfilePage/changeSubsidiary/messages/formValidation/limit',
        },
      }}
      validationSchema={{
        isSupervisionActivated: { type: 'boolean' },
        supervisor: {
          type: 'custom',
          check: function myCheck(
            value: string,
            schema: any,
            field: any,
            parent: CustomerProfileEdit,
          ): boolean | string[] {
            if (parent.isSupervisionActivated && typeof value !== 'string') {
              // eslint-disable-next-line react/no-this-in-sfc
              // @ts-ignore
              return this.makeError('supervisor');
            }
            return true;
          },
        },
        limit: {
          type: 'custom',
          check: function myCheck(
            value: string,
            schema: any,
            field: any,
            parent: CustomerProfileEdit,
          ): boolean | string[] {
            if (parent.isSupervisionActivated && (typeof value === 'number' && value > 0) === false) {
              // eslint-disable-next-line react/no-this-in-sfc
              // @ts-ignore
              return this.makeError('limit');
            }
            return true;
          },
        },
      }}
    >
      {(formikProps) => (
        <WrapDialogForm
          {...formikProps}
          dialogTitle={t('customerProfilePage/changeSubsidiary/dialogTitle')}
          open={open}
          onClose={onClose}
          dialogProps={{
            fullWidth: true,
            maxWidth: 'sm',
          }}
          render={() => {
            const { errors, onChange: handleChange, values } = formikProps;

            return (
              <>
                <CheckboxField
                  fullWidth
                  label={t('common/approver')}
                  onChange={handleChange('isSupervisionActivated')}
                  value={values.isSupervisionActivated}
                  switch
                />
                {values.isSupervisionActivated && (
                  <>
                    <CustomerProfilePageSubsidiarySupervisorSelector
                      fullWidth
                      label={t('common/approver')}
                      contactId={customerId}
                      value={values.supervisor}
                      supervisors={resContactSupervisors.data || []}
                      error={errors.supervisor}
                      onChange={handleChange('supervisor')}
                    />
                    <PriceField
                      currency={currency}
                      label={t('orders/order/priceLimit')}
                      helperText={''}
                      value={values.limit}
                      error={errors.limit}
                      onChange={handleChange('limit')}
                      fullWidth
                    />
                  </>
                )}
              </>
            );
          }}
        />
      )}
    </FormikExtended>
  );
};

export default CustomerProfilePageSubsidiaryEditDialog;
